import { FarmContractContainer } from 'common/models/crypto';
import { Nullable } from 'common/models/optional';
import lf from 'localforage';
import remove from 'lodash/remove';
import { Subject } from 'rxjs';
import omit from 'lodash/omit';
import { waitForTransactionReceipt, getBlockNumber } from '@wagmi/core';
import { Address } from 'viem';
import { IchiContract, MintableToken } from 'constants/contracts';
import { wagmiConf } from 'containers/WalletProvider/WalletProvider';

export namespace StorageUtils {
  export const txSubject$ = new Subject();

  export type StoredTx = {
    hash: string;
    functionName: string;
    labels: {
      executing: string;
      success: string;
      error: string;
    };
    value: any;
    txReceipt: any;
    txStartBlock: bigint;
    chainId: number;
    accountAddress: string;
  };

  export type ContractContainer = {
    contractContainer:
      | MintableToken
      | FarmContractContainer
      | IchiContract<any>;
  };

  export type AccountAddressArg = {
    accountAddress: string;
  };

  export type ChainIdArg = {
    chainId: number;
  };

  export type FunctionNameArg = {
    functionName: string;
  };

  export type LabelsArg = {
    labels: {
      executing: string;
      success: string;
      error: string;
    };
  };

  export type TxHashArg = {
    hash: string;
  };

  export type StoredTxArg = {
    storedTx: StoredTx;
  };

  export type ValueArg<T = any> = {
    value: T;
  };

  export function makeAccountTxKey(
    accountAddress: string,
    chainId: number
  ): string {
    return `tx_${accountAddress}_${chainId}`;
  }

  export async function getAccountTxs(
    accountAddress: string,
    chainId: number
  ): Promise<StoredTx[]> {
    const key = makeAccountTxKey(accountAddress, chainId);
    const value = (await lf.getItem<StoredTx[]>(key)) || [];
    return value;
  }

  export async function storeAccountTxs(
    accountAddress: string,
    chainId: number,
    value: StoredTx[]
  ): Promise<StoredTx[]> {
    const key = makeAccountTxKey(accountAddress, chainId);
    const storedValue = await lf.setItem<StoredTx[]>(key, value);
    txSubject$.next(value);
    return storedValue;
  }

  // export async function getAcountTxs(args: GeneralApiArgs) {
  //   const key = StorageUtils.makeTxStorageKey(args);
  //   const value = await lf.getItem<string>(key);
  // }

  // export function makeTxStorageKey(args: GeneralApiArgs & FunctionNameArg): string {
  //   return `tx_hash_${args.chainId}_${args.accountAddress}_${args.functionName}`;
  // }

  export async function getTx(
    args: AccountAddressArg & ChainIdArg & FunctionNameArg
  ): Promise<Nullable<StoredTx>> {
    // Get the tx hashes for the account
    let accountTxHashes = await StorageUtils.getAccountTxs(
      args.accountAddress,
      args.chainId
    );

    if (!accountTxHashes || accountTxHashes.length === 0) {
      return null;
    }
    // Make the key for this tx
    // const key = StorageUtils.makeTxStorageKey(args);

    // And get it from the account hashes
    const value =
      accountTxHashes.find(
        (t: StoredTx) => t.functionName === args.functionName
      ) || null;

    if (value && !value.hash) {
      console.error(
        `Found stored tx for ${value.functionName} but no associated hash, removing this entry`
      );
      accountTxHashes = remove(
        accountTxHashes,
        (t) => t.functionName === args.functionName
      );
      await StorageUtils.storeAccountTxs(
        args.accountAddress,
        args.chainId,
        accountTxHashes
      );
      return null;
    }

    return value;
  }

  export async function storeTx(
    args: AccountAddressArg &
      ChainIdArg &
      FunctionNameArg &
      LabelsArg &
      TxHashArg &
      ValueArg
  ): Promise<Nullable<StoredTx>> {
    if (!args.hash) {
      // TODO: We may not want to throw an Error here, not clear yet
      throw new Error('No tx value given, cannot store a null tx.');
    }
    // Make the individual hash key
    // const key = StorageUtils.makeTxStorageKey(args);

    // Get the tx hashes for the account
    const accountTxHashes = await StorageUtils.getAccountTxs(
      args.accountAddress,
      args.chainId
    );
    const existingEntry =
      accountTxHashes.find((t) => t.functionName === args.functionName) || null;
    if (existingEntry) {
      console.warn(
        `${existingEntry.hash} already in local storage account hashes`
      );
      return existingEntry;
    }

    const txReceipt = await waitForTransactionReceipt(wagmiConf, { hash: args.hash as Address });
    const txStartBlock = await getBlockNumber(wagmiConf);

    const storedTx: StoredTx = {
      hash: args.hash,
      functionName: args.functionName,
      labels: args.labels,
      value: args.value,
      chainId: args.chainId,
      accountAddress: args.accountAddress,
      // localforace won't serialize the wait
      txReceipt: omit(txReceipt, 'wait'),
      txStartBlock,
    };

    accountTxHashes.push(storedTx);
    await StorageUtils.storeAccountTxs(
      args.accountAddress,
      args.chainId,
      accountTxHashes
    );
    return storedTx;
  }

  export async function removeTx(
    args: AccountAddressArg & ChainIdArg & FunctionNameArg & TxHashArg
  ): Promise<Nullable<StoredTx>> {
    // Get the tx hashes for the account
    const accountTxHashes = await StorageUtils.getAccountTxs(
      args.accountAddress,
      args.chainId
    );

    const existingEntry =
      accountTxHashes.find(
        (t) => t.functionName === args.functionName && t.hash === args.hash
      ) || null;
    if (existingEntry) {
      remove(
        accountTxHashes,
        (t) => t.functionName === args.functionName && t.hash === args.hash
      );
      await StorageUtils.storeAccountTxs(
        args.accountAddress,
        args.chainId,
        accountTxHashes
      );
      return existingEntry;
    }

    console.warn(
      `No stored tx associated with ${args.functionName} and ${args.hash}, that's odd...`
    );
    return null;
  }

  export async function removeTxByHash(
    args: AccountAddressArg & ChainIdArg & TxHashArg
  ): Promise<Nullable<StoredTx>> {
    // Get the tx hashes for the account
    const accountTxHashes = await StorageUtils.getAccountTxs(
      args.accountAddress,
      args.chainId
    );

    const existingEntry =
      accountTxHashes.find((t) => t.hash === args.hash) || null;
    if (existingEntry) {
      remove(accountTxHashes, (t) => t.hash === args.hash);
      await StorageUtils.storeAccountTxs(
        args.accountAddress,
        args.chainId,
        accountTxHashes
      );
      // This doesn't have the intended effect, what's happening if you close the stake modal is
      // the machine is destroyed and apparently it's tied to this code so this code is never executed.
      // This isn't an issue if the user leaves the modal open, only if closes, some potential ways around
      // may be to spawn the machine with a unique UUID in a useEffect [] in the modal container.
      // StorageUtils.txSubject$.next(existingEntry);
      return existingEntry;
    }

    console.warn(`No stored tx with hash ${args.hash}, that's odd...`);
    return null;
  }
}
